import { useState } from "react";

export type FieldType =
  | "text"
  | "textarea"
  | "number"
  | "checkbox"
  | "select"
  | "tags"
  | "datetime"
  | "upload"
  | "checklist";

export type FieldValue = string | number | boolean | null | string[];
export type FormValues = Record<string, FieldValue>;

/**
 * Values to merge into the form after an upload or a field action: `fill`
 * overwrites (e.g. a new image's thumbnail and size), `fillIfEmpty` only fills
 * what the admin has not typed yet (e.g. a title).
 */
export interface FormPatch {
  fill?: FormValues;
  fillIfEmpty?: FormValues;
}

export interface FieldDef {
  name: string;
  label: string;
  type: FieldType;
  required?: boolean;
  /** Choices for `select`, and the known entries for `checklist`. */
  options?: { value: string; label: string }[];
  placeholder?: string;
  help?: string;
  /** For `upload` fields: the file picker's accept filter (e.g. "image/*"). */
  accept?: string;
  /** Show the field only when this returns true (e.g. share list only when restricted). */
  showIf?: (values: FormValues) => boolean;
  /** A button beside the field that computes values for other fields (e.g. "Fetch details"). */
  action?: { label: string; run: (values: FormValues) => Promise<FormPatch> };
  /** For `checklist`: what to say when there are no options at all. */
  emptyHint?: string;
}

interface AdminFormProps {
  title: string;
  fields: FieldDef[];
  initial: FormValues;
  submitLabel?: string;
  onSubmit: (values: FormValues) => Promise<void>;
  onCancel: () => void;
  /**
   * Uploads a file for an upload field and returns its public URL — or the URL
   * plus values for other fields (a photo upload fills in its thumbnail and
   * size).
   */
  onUpload?: (file: File, field: FieldDef) => Promise<string | ({ url: string } & FormPatch)>;
}

/** Merge a patch into form values, respecting fill-vs-fill-if-empty. */
export function applyPatch(values: FormValues, patch: FormPatch): FormValues {
  const next = { ...values, ...(patch.fill ?? {}) };
  for (const [key, value] of Object.entries(patch.fillIfEmpty ?? {})) {
    const current = next[key];
    const empty =
      current == null || (Array.isArray(current) ? current.length === 0 : String(current).trim() === "");
    if (empty && value != null && value !== "") next[key] = value;
  }
  return next;
}

/**
 * A modal form driven by field definitions. Holds its own value state, performs
 * a light required-field check, surfaces submit errors inline (keeping the modal
 * open), and hands the raw values back to the caller, which maps them to the API
 * request shape.
 */
export function AdminForm({
  title,
  fields,
  initial,
  submitLabel = "Save",
  onSubmit,
  onCancel,
  onUpload,
}: AdminFormProps) {
  const [values, setValues] = useState<FormValues>(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const [actionBusy, setActionBusy] = useState("");

  function set(name: string, value: FieldValue) {
    setValues((prev) => ({ ...prev, [name]: value }));
  }

  function patch(p: FormPatch) {
    setValues((prev) => applyPatch(prev, p));
  }

  async function runAction(f: FieldDef) {
    if (!f.action) return;
    setActionBusy(f.name);
    setError("");
    try {
      patch(await f.action.run(values));
    } catch (err) {
      setError(err instanceof Error ? err.message : `${f.action.label} failed.`);
    } finally {
      setActionBusy("");
    }
  }

  const visible = fields.filter((f) => !f.showIf || f.showIf(values));

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    for (const f of visible) {
      if (f.required && f.type !== "checkbox") {
        const v = values[f.name];
        if (v == null || (Array.isArray(v) ? v.length === 0 : String(v).trim() === "")) {
          setError(`${f.label} is required.`);
          return;
        }
      }
    }
    setBusy(true);
    setError("");
    try {
      await onSubmit(values);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed.");
      setBusy(false);
    }
  }

  return (
    <div
      className="admin-modal__backdrop"
      role="dialog"
      aria-modal="true"
      onMouseDown={onCancel}
    >
      <div className="admin-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="admin-modal__head">
          <h2>{title}</h2>
          <button
            className="admin-link-btn"
            style={{ color: "#fff" }}
            onClick={onCancel}
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div className="admin-modal__body">
          {error && (
            <p className="notice notice--err" role="alert">
              ✕ {error}
            </p>
          )}
          <form className="form" onSubmit={submit} noValidate style={{ maxWidth: "none" }}>
            {visible.map((f) => (
              <div className="field" key={f.name}>
                <label htmlFor={`f-${f.name}`}>
                  {f.label}
                  {f.required ? " *" : ""}
                </label>
                <FieldInput
                  field={f}
                  value={values[f.name]}
                  onChange={set}
                  onPatch={patch}
                  onUpload={onUpload}
                />
                {f.action && (
                  <button
                    type="button"
                    className="btn btn--ghost field__action"
                    onClick={() => void runAction(f)}
                    disabled={!!actionBusy}
                  >
                    {actionBusy === f.name ? "Working…" : f.action.label}
                  </button>
                )}
                {f.help && (
                  <span
                    className="mono"
                    style={{ fontSize: "0.7rem", color: "var(--ink-500)" }}
                  >
                    {f.help}
                  </span>
                )}
              </div>
            ))}
            <div className="admin-actions">
              <button
                type="button"
                className="btn btn--ghost"
                onClick={onCancel}
                disabled={busy}
              >
                Cancel
              </button>
              <button type="submit" className="btn" disabled={busy}>
                {busy ? "Saving…" : submitLabel}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function FieldInput({
  field,
  value,
  onChange,
  onPatch,
  onUpload,
}: {
  field: FieldDef;
  value: FieldValue;
  onChange: (name: string, value: FieldValue) => void;
  onPatch: (patch: FormPatch) => void;
  onUpload?: AdminFormProps["onUpload"];
}) {
  const id = `f-${field.name}`;
  switch (field.type) {
    case "upload":
      return (
        <UploadField
          field={field}
          value={value}
          onChange={onChange}
          onPatch={onPatch}
          onUpload={onUpload}
        />
      );
    case "checklist":
      return <Checklist field={field} value={value} onChange={onChange} />;
    case "textarea":
      return (
        <textarea
          id={id}
          value={String(value ?? "")}
          placeholder={field.placeholder}
          onChange={(e) => onChange(field.name, e.target.value)}
        />
      );
    case "number":
      return (
        <input
          id={id}
          type="number"
          value={value == null ? "" : String(value)}
          placeholder={field.placeholder}
          onChange={(e) =>
            onChange(field.name, e.target.value === "" ? null : Number(e.target.value))
          }
        />
      );
    case "checkbox":
      return (
        <input
          id={id}
          type="checkbox"
          checked={!!value}
          onChange={(e) => onChange(field.name, e.target.checked)}
          style={{ width: "auto" }}
        />
      );
    case "select":
      return (
        <select
          id={id}
          value={String(value ?? "")}
          onChange={(e) => onChange(field.name, e.target.value)}
        >
          {(field.options ?? []).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      );
    case "datetime":
      return (
        <input
          id={id}
          type="datetime-local"
          value={String(value ?? "")}
          onChange={(e) => onChange(field.name, e.target.value)}
        />
      );
    case "tags":
    case "text":
    default:
      return (
        <input
          id={id}
          type="text"
          value={String(value ?? "")}
          placeholder={field.placeholder}
          onChange={(e) => onChange(field.name, e.target.value)}
        />
      );
  }
}

/**
 * A list of checkboxes whose value is the array of ticked option values. Values
 * already set that are not among the options (e.g. an email shared with before
 * that person had an account) stay listed and ticked, so saving the form never
 * silently drops them.
 */
function Checklist({
  field,
  value,
  onChange,
}: {
  field: FieldDef;
  value: FieldValue;
  onChange: (name: string, value: FieldValue) => void;
}) {
  const selected = Array.isArray(value) ? value : [];
  const known = field.options ?? [];
  const extra = selected
    .filter((v) => !known.some((o) => o.value === v))
    .map((v) => ({ value: v, label: `${v} (no account)` }));
  const options = [...known, ...extra];

  function toggle(v: string, on: boolean) {
    onChange(field.name, on ? [...selected, v] : selected.filter((s) => s !== v));
  }

  if (options.length === 0) {
    return <p className="checklist__empty">{field.emptyHint ?? "Nothing to choose from."}</p>;
  }
  return (
    <div className="checklist" id={`f-${field.name}`} role="group" aria-label={field.label}>
      {options.map((o) => (
        <label key={o.value} className="checklist__item">
          <input
            type="checkbox"
            checked={selected.includes(o.value)}
            onChange={(e) => toggle(o.value, e.target.checked)}
            style={{ width: "auto" }}
          />{" "}
          {o.label}
        </label>
      ))}
    </div>
  );
}

/**
 * A URL field with an "Upload" button beside it: paste a link, or upload a file
 * and the returned public URL fills the field. Used for photo/video/cover URLs
 * so admins can add content by link OR by upload. An upload handler may also
 * return values for other fields (a photo's thumbnail and dimensions).
 */
function UploadField({
  field,
  value,
  onChange,
  onPatch,
  onUpload,
}: {
  field: FieldDef;
  value: FieldValue;
  onChange: (name: string, value: FieldValue) => void;
  onPatch: (patch: FormPatch) => void;
  onUpload?: AdminFormProps["onUpload"];
}) {
  const id = `f-${field.name}`;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function pick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file || !onUpload) return;
    setBusy(true);
    setError("");
    try {
      const result = await onUpload(file, field);
      if (typeof result === "string") {
        onChange(field.name, result);
      } else {
        const { url, ...rest } = result;
        onChange(field.name, url);
        onPatch(rest);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div className="upload-field">
        <input
          id={id}
          type="text"
          value={String(value ?? "")}
          placeholder={field.placeholder ?? "Paste a URL, or upload →"}
          onChange={(e) => onChange(field.name, e.target.value)}
        />
        <label className="btn btn--ghost upload-field__btn">
          {busy ? "Uploading…" : "Upload…"}
          <input
            type="file"
            accept={field.accept}
            onChange={pick}
            disabled={busy || !onUpload}
            hidden
          />
        </label>
      </div>
      {error && (
        <span className="notice notice--err" style={{ display: "block", marginTop: "0.3rem" }}>
          ✕ {error}
        </span>
      )}
    </div>
  );
}
